// src/components/AppErrorBoundary.tsx

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { ErrorService } from '@/lib/errors';
import type { ErrorContext } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { isStaleAssetLoadError, reloadOnceForStaleAssetLoad } from '@/lib/staleAssetReload';

type BoundaryLevel = 'root' | 'zone';

interface Props {
  children: ReactNode;
  level?: BoundaryLevel;
  /** Contexte PostHog — identifiants métier, jamais de contenu */
  errorContext?: ErrorContext;
  /** Override le fallback par défaut */
  fallback?: ReactNode;
  /** Callback appelé après reset du boundary */
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  isStaleAssetLoadError: boolean;
}

/**
 * Error Boundary générique à deux niveaux.
 *
 * - root : fallback pleine page avec bouton recharger
 * - zone : message inline discret avec bouton réessayer
 *
 * Remonte vers ErrorService → PostHog hors récupération de chunks Vite périmés.
 */
export class AppErrorBoundary extends Component<Props, State> {
  static defaultProps = { level: 'zone' };

  state: State = { hasError: false, isStaleAssetLoadError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, isStaleAssetLoadError: isStaleAssetLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const isStaleAssetError = isStaleAssetLoadError(error);
    if (reloadOnceForStaleAssetLoad(error)) return;

    // Vite can dispatch `vite:preloadError` and mark a stale chunk for reload
    // before React's boundary catches the same rejected lazy import. In that
    // case this is deploy recovery, not an app crash to send to PostHog.
    if (isStaleAssetError) return;

    ErrorService.handleBoundaryError(error, {
      ...this.props.errorContext,
      action: `error_boundary_${this.props.level ?? 'zone'}`,
    }, info.componentStack ?? undefined);
  }

  handleRetry = () => {
    this.setState({ hasError: false, isStaleAssetLoadError: false });
    this.props.onRetry?.();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    if (this.props.level === 'root') {
      const description = this.state.isStaleAssetLoadError
        ? "Une mise à jour de l'application est disponible. Rechargez la page pour charger la dernière version."
        : "Notre équipe en a été informée. Vous pouvez recharger la page.";

      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-8 text-center">
          <p className="text-grey-800 text-lg font-medium">
            Un problème technique est survenu.
          </p>
          <p className="text-grey-600 text-sm max-w-sm">
            {description}
          </p>
          <Button onClick={() => window.location.reload()}>
            Recharger
          </Button>
        </div>
      );
    }

    // level === 'zone'
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-grey-600 text-sm">
          Impossible d'afficher cette section.
        </p>
        <Button variant="outline" size="sm" onClick={this.handleRetry}>
          Réessayer
        </Button>
      </div>
    );
  }
}
